<template>
  <div>
    <div class="rangeWrapper">
      <div class="sliderWrapper">
        <v-range-slider
          :value="range"
          :max="maxRange"
          :disabled="disabled"
          @change="onChange"
          thumb-label
          class="slider"
        >
          <template v-slot:thumb-label="props">
            <span class="thumbLabel text-no-wrap">{{
              displayRange(props.value)
            }}</span>
          </template>
        </v-range-slider>
      </div>
    </div>
    <div class="rangeLabels">
      <div
        v-for="segment in reversedSegments"
        :key="segment.text"
        class="rangeLabel"
      >
        {{ segment.text }}
      </div>
    </div>
  </div>
</template>

<script>
import head from 'lodash/head'
import tail from 'lodash/tail'

/**
 * キャンペーンで設定できる項目をコピーしてシミュレーションでも使う
 * という関係性からキャンペーンからのimportを許容する
 */
import { TargetVisitorConsts } from '@/models/CampaignOperation/Constants'

const MAX_VALUE = 1000
const MIN_VALUE = 0
const MINUTES_OF_HOUR = 60
const MINUTES_OF_DAY = 60 * 12

// 分←→時、分←→日変換
const daysToMinutes = days => days * 24 * 60
const minutesToHours = minutes => Math.floor(minutes / 60)
const minutesToDays = minutes => Math.floor(minutesToHours(minutes) / 24)
const ONE_DAY = daysToMinutes(1)

class SliderSegment {
  constructor({ text, minuteCount, maxMinutes, stepCount, accuracy }) {
    this.text = text
    this.minuteCount = minuteCount
    this.maxMinutes = maxMinutes
    this.stepCount = stepCount
    this.accuracy = accuracy
  }

  get stepByMinute() {
    if (this.minuteCount === 0) return 0
    return this.stepCount / this.minuteCount
  }
}

const segmentConfs = [
  {
    text: '現在',
    minuteCount: 0,
    maxMinutes: 0,
  },
  {
    text: '1日',
    minuteCount: daysToMinutes(1),
    maxMinutes: daysToMinutes(1),
    accuracy: MINUTES_OF_HOUR,
  },
  {
    text: '1ヶ月',
    minuteCount: daysToMinutes(30 - 1),
    maxMinutes: daysToMinutes(30),
    accuracy: MINUTES_OF_DAY,
  },
  {
    text: '3ヶ月',
    minuteCount: daysToMinutes(90 - 30),
    maxMinutes: daysToMinutes(90),
    accuracy: MINUTES_OF_DAY,
  },
  {
    text: '半年',
    minuteCount: daysToMinutes(180 - 90),
    maxMinutes: daysToMinutes(180),
    accuracy: MINUTES_OF_DAY,
  },
  {
    text: '１年',
    minuteCount: daysToMinutes(365 - 180),
    maxMinutes: daysToMinutes(365),
    accuracy: MINUTES_OF_DAY,
  },
]

const segmentHasMinutesCount = segmentConfs.length - 1
const stepCountBySegment = MAX_VALUE / segmentHasMinutesCount
const segments = segmentConfs.map(segmentConfig => {
  const stepCount = segmentConfig.minuteCount > 0 ? stepCountBySegment : 0
  return new SliderSegment({ ...segmentConfig, stepCount })
})

// slider value ←→ 右端を始点とした場合の値
const invertValue = value => MAX_VALUE - value
const invertedValueToMinutes = (invertedValue, segments) => {
  if (segments.length === 0) throw new Error('nil segments')
  const s = head(segments)
  const ss = tail(segments)
  if (invertedValue <= s.stepCount) {
    return s.stepByMinute > 0
      ? Math.floor(Math.ceil(invertedValue / s.stepByMinute) / s.accuracy) *
          s.accuracy
      : 0
  } else {
    return (
      invertedValueToMinutes(invertedValue - s.stepCount, ss) + s.minuteCount
    )
  }
}
const minutesToInvertedValue = (minutes, segments) => {
  if (segments.length === 0) throw new Error('nil segments')
  const s = head(segments)
  const ss = tail(segments)
  if (minutes <= s.minuteCount) {
    return Math.floor(s.stepByMinute * minutes)
  } else {
    return minutesToInvertedValue(minutes - s.minuteCount, ss) + s.stepCount
  }
}

export default {
  name: 'AppDeliveryTargetAdvancedSettingPeriodSlider',
  props: {
    // minutes
    value: {
      type: Number,
      default: TargetVisitorConsts.MAX_MINUTES,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      range: [0, MAX_VALUE],
    }
  },
  computed: {
    maxRange() {
      return MAX_VALUE
    },
    segments() {
      return segments
    },
    reversedSegments() {
      return [...segments].reverse()
    },
    minutes() {
      return this.calcMinutes(this.range[0])
    },
  },
  watch: {
    value(newMinutes) {
      this.setMinutes(newMinutes)
    },
  },
  created() {
    this.setMinutes(this.value)
  },
  methods: {
    setValue(value) {
      this.range = [value, MAX_VALUE]
    },
    setMinutes(minutes) {
      this.setValue(invertValue(minutesToInvertedValue(minutes, segments)))
    },
    calcMinutes(value) {
      return invertedValueToMinutes(invertValue(value), segments)
    },
    displayRange(value) {
      // 1日未満なら1〜23時、1日以上は日数を表示する
      const minutes = this.calcMinutes(value)
      if (minutes === 0) {
        return `現在`
      } else if (minutes < ONE_DAY) {
        return `${minutesToHours(minutes)}H`
      } else {
        return minutesToDays(minutes)
      }
    },
    onChange(e) {
      const value = Math.min(e[0], invertValue(MIN_VALUE))
      this.setValue(value)
      const minutes = this.minutes
      this.onChangeMinutes(minutes)
      this.$emit('input', minutes)
    },
    onChangeMinutes(minutes) {
      const value = invertValue(
        minutesToInvertedValue(Number(minutes), segments),
      )
      this.range = [value, MAX_VALUE]
    },
  },
}
</script>

<style scoped>
.rangeWrapper {
  padding-left: 15px;
  padding-right: 15px;
}

.rangeLabels {
  display: flex;
  width: 100%;
  justify-content: space-between;
}

.sliderWrapper {
  position: relative;
}

.slideBlocker {
  position: absolute;
  right: -9px;
  top: 7px;
  width: 17px;
  height: 17px;
  background-color: rgba(0, 0, 0, 0);
  z-index: 10;
}

.thumbLabel {
  font-size: 12px;
}
</style>
